/**
 * React Query hooks for notifications
 *
 * This module provides React Query hooks for managing notifications state
 * including fetching unread count and mutations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  type ListNotificationsParams,
  type ListNotificationsResponse,
} from '../api/notifications';

/**
 * Notification query keys factory
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params: ListNotificationsParams) =>
    [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
};

/** Refetch interval for notifications (ms) so list and badge update without reopening */
const NOTIFICATION_REFETCH_INTERVAL = 30_000;

/**
 * Hook to fetch notifications with pagination
 */
export function useNotifications(params: ListNotificationsParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => {
      const result = await listNotifications(params);
      return result;
    },
    refetchInterval: NOTIFICATION_REFETCH_INTERVAL,
  });
}

/**
 * Hook to fetch unread count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const result = await getUnreadCount();
      return result.count;
    },
    refetchInterval: NOTIFICATION_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userNotificationId: string) => {
      const result = await markAsRead(userNotificationId);
      return result;
    },
    onSuccess: () => {
      // Invalidate all notification queries to refetch
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await markAllAsRead();
      return result;
    },
    onSuccess: () => {
      // Invalidate all notification queries to refetch
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Hook to delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userNotificationId: string) => {
      const result = await deleteNotification(userNotificationId);
      return result;
    },
    onSuccess: () => {
      // Invalidate all notification queries to refetch
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
